import { getUpdatedAxisRangeBasedOnTicks } from '@thoughtspot/formatter-service';
import { isHideChartComponentsForSmallViewport, isRadiantThemeEnabled, } from '@thoughtspot/session-service';
import _ from 'lodash';
import { createSeriesColorResolver } from '../core/chart-color-utils';
import { ErrorTypes } from '../core/chart-data-validation';
import { getAxisAdvancedPropertiesById, getAxisProperties, getChartProperties, getChartType, getColorBySerieName, getColorPaletteFromLegendOverrides, getColumn, getSeriesColorsMap, getShowStackedLabels, getUpdatedSeriesColor, getXAxes, getYAxes, isZoomedIn, shouldShowDataLabels, } from '../core/chart-model-utils';
import * as chartTypeSpecificationService from '../core/chart-type-specification-service';
import { configOptions, hasNativeLegend, hasPersistentLegend, isXAxisVertical, } from '../core/chart-type-specification-service';
import { formattingConstants, getDefaultLabelEnablementValues, hasEnoughSpaceForDataLabels, shouldShowPropertyBasedOnDimension, } from '../core/chart-ui-utils';
import { getContextMenuInputPoint } from '../core/context-menu-point-utils';
import { updateClipProperty } from '../core/point-selection-service/util';
import { getSerie } from '../data/data-util';
import { getEnvFlags } from '../env';
import { ChartEventTypes } from '../index';
import { ChartThemeService, offGridline, onGridline, } from '../theme/chart-theme-service';
import { AxisTypes, ChartResponsiveLayout, ChartType, } from '../types';
import { createLogger } from '../utils/logger';
import { BlinkHighchart } from './blink-highchart';
import { BlinkHighchartConfig } from './blink-highchart-config';
import { getHighchartScreenConfig } from './chart-theme-screen-service';
import { updateDataLabels } from './data-label.util';
import { bindAxisEvents } from './highchart-axis-renderer';
import { generateDarkModePalette, isDarkModeActive, subscribeToThemeChanges, } from '@thoughtspot/radiant-react/widgets/color';
import { getHighchartsObject } from './highchart-init';
import { MobileHighchartConfig } from './mobile-highchart-config';
var Orientation;
(function (Orientation) {
    Orientation["Top"] = "top";
    Orientation["Bottom"] = "bottom";
    Orientation["Left"] = "left";
    Orientation["Right"] = "right";
})(Orientation || (Orientation = {}));
const logger = createLogger('highchart-renderer');
function setChartContainer(highchartConfig, el) {
    _.set(highchartConfig, ['chart', 'renderTo'], el);
}
function executeEventHandler(eventsMap, eventType, args = []) {
    const eventHandler = eventsMap.get(eventType);
    if (_.isFunction(eventHandler)) {
        eventHandler.call(null, ...args);
        return;
    }
    logger.warn(`Chart Event Callback not implemented for ${eventType}.`);
}
function bringSelectedSeriesToFront(point) {
    if (point && point.series && point.series.group) {
        point.series.group.toFront();
    }
}
function getHighchartOnSeriesClickHandler(handler, shouldBringSelectedSeriesToFront, viz) {
    return evt => {
        if (shouldBringSelectedSeriesToFront) {
            bringSelectedSeriesToFront(evt.point);
        }
        if (_.isFunction(handler)) {
            const clickedPoint = evt.point && getContextMenuInputPoint(viz, evt.point);
            handler(evt, clickedPoint);
        }
    };
}
const getAxisPropertiesVisibility = (dimensions) => {
    const shouldHideChartComponents = isHideChartComponentsForSmallViewport();
    if (!shouldHideChartComponents) {
        const { shouldShowXAxisLabels, shouldShowXAxisName, shouldShowYAxisLabels, shouldShowYAxisName, } = getDefaultLabelEnablementValues();
        return {
            showXAxisLabels: shouldShowXAxisLabels,
            showXAxisName: shouldShowXAxisName,
            showYAxisLabels: shouldShowYAxisLabels,
            showYAxisName: shouldShowYAxisName,
        };
    }
    const { SHOW_X_AXIS_LABEL, SHOW_Y_AXIS_LABEL } = formattingConstants;
    const { SHOW_X_AXIS_NAME, SHOW_Y_AXIS_NAME } = formattingConstants;
    return {
        showXAxisLabels: shouldShowPropertyBasedOnDimension(dimensions, SHOW_X_AXIS_LABEL),
        showXAxisName: shouldShowPropertyBasedOnDimension(dimensions, SHOW_Y_AXIS_LABEL),
        showYAxisLabels: shouldShowPropertyBasedOnDimension(dimensions, SHOW_X_AXIS_NAME),
        showYAxisName: shouldShowPropertyBasedOnDimension(dimensions, SHOW_Y_AXIS_NAME),
    };
};
export function generateHighchartConfig(chartModel, dataModel, boundingBox, eventsMap, config, env, onRender = _.noop) {
    const Highcharts = getHighchartsObject();
    ChartThemeService.useDefaultTheme(Highcharts, env.flags);
    const chartConfigOptions = configOptions(getChartType(chartModel), chartModel);
    const shouldBringSelectedSeriesToFront = chartConfigOptions.shouldBringSelectedSeriesToFront;
    const shouldHideChartComponents = isHideChartComponentsForSmallViewport();
    const updateZoomAttributes = eventsMap.get(ChartEventTypes.UpdateZoomEvent);
    const { showXAxisLabels, showXAxisName, showYAxisLabels, showYAxisName, } = getAxisPropertiesVisibility(boundingBox);
    const chartConfig = new BlinkHighchartConfig(chartModel, dataModel, {
        containerDimensions: boundingBox,
        shouldShowXAxisLabels: showXAxisLabels,
        shouldShowYAxisLabels: showYAxisLabels,
        shouldShowXAxisName: showXAxisName,
        shouldShowYAxisName: showYAxisName,
        shouldShowDataLabels: shouldShowDataLabels(chartModel, dataModel, getEnvFlags().maxLabelCount, getEnvFlags().dataBatchSize, shouldHideChartComponents ? boundingBox : null),
        disableTransformations: config.disableTransformations,
        disableColorRotation: config.disableColorRotation,
        isDataSourceDefault: config.isDataSourceDefault,
        hideAxisIcon: config.hideAxisIcon,
    }, env);
    const xGridlineEnabled = config.xGridlineEnabled;
    const yGridlineEnabled = config.yGridlineEnabled;
    const shouldShowTotalRows = config.shouldShowDataRowDisplayedCount;
    const afterSetExtremesDebounce = _.debounce($event => {
        const axisesExtremes = $event.target.chart.getAxisesExtremes();
        updateZoomAttributes(axisesExtremes, !!($event.userMax || $event.userMin));
    }, 300);
    chartConfig
        .configureDefaults()
        .setColors()
        .disableZoom()
        .configureXAxis(xGridlineEnabled)
        .configureYAxis(yGridlineEnabled)
        .configureAccessibility()
        .configureColorAxis()
        .configureSeries(getHighchartOnSeriesClickHandler(eventsMap.get(ChartEventTypes.PointClick), shouldBringSelectedSeriesToFront, chartModel), getHighchartOnSeriesClickHandler(eventsMap.get(ChartEventTypes.ContextClick), shouldBringSelectedSeriesToFront, chartModel), getHighchartOnSeriesClickHandler(eventsMap.get(ChartEventTypes.PointDoubleClick), shouldBringSelectedSeriesToFront, chartModel), getHighchartOnSeriesClickHandler(eventsMap.get(ChartEventTypes.MouseOver), shouldBringSelectedSeriesToFront, chartModel), getHighchartOnSeriesClickHandler(eventsMap.get(ChartEventTypes.MouseOut), shouldBringSelectedSeriesToFront, chartModel), env.flags.maxChartLegendCardinality, (series, visible) => { }, config.disableHighlightOnHover)
        .configureDataLabels()
        .configureConnectNulls()
        .configurePlotLines()
        .setAfterSetExtremesCallback($event => {
        if ($event.trigger && $event.trigger === 'zoom') {
            afterSetExtremesDebounce($event);
        }
    })
        .configureChartTitle(shouldShowTotalRows, config.shouldShowHighchartTitle)
        .configureWindowing()
        .configureLineOptions()
        .configureGenericTooltip()
        .configureExporting()
        .configureLegend((param) => { }, config.isLegendEnabled, hasPersistentLegend(getChartType(chartModel)), (serie, anchor) => {
    })
        .configureAxisScroll(getEnvFlags().allowMobileOverride)
        .configureChartOptions(getHighchartOnSeriesClickHandler(eventsMap.get(ChartEventTypes.ChartAreaClick), shouldBringSelectedSeriesToFront, chartModel))
        .configureSerieGetter(serieName => getSerie(dataModel, serieName))
        .configureRenderCallback(onRender);
    if (config.optSupportAnimation) {
        chartConfig.configureAnimation();
    }
    chartConfig.configureOverrides();
    if (env.flags.allowMobileOverride) {
        const mobileOverrides = new MobileHighchartConfig(chartModel);
        mobileOverrides.dropGraySelected().setScatterChartOverrides();
        _.merge(chartConfig._highchartConfig, mobileOverrides._highchartConfig);
    }
    const chartAxisDetails = {
        x: chartConfig._highchartConfig.xAxis,
        y: chartConfig._highchartConfig.yAxis,
    };
    const chartConfigForScreen = getHighchartScreenConfig(chartConfig._containerDimensions.width, {}, chartAxisDetails, config.shouldScaleFonts, chartModel, dataModel, config === null || config === void 0 ? void 0 : config.enablePiechartAnimation, chartConfig._containerDimensions.height);
    if (hasNativeLegend(chartModel)) {
        chartConfigForScreen.config.legend.enabled =
            chartConfigForScreen.meta.legendEnabled;
    }
    chartConfig._highchartConfig = _.merge(chartConfig._highchartConfig, chartConfigForScreen.config);
    if (getEnvFlags().isHighchartCustomisationEnabled) {
        chartConfig.configureHighchartCustomisationSettings();
        chartConfig.configAdvancedAxisSettings();
        chartConfig.configAdvancedColumnSettings();
    }
    return chartConfig;
}
export function updateExtremes(axis, extremes) {
    if (!extremes) {
        return;
    }
    const { dataMin, dataMax, min, max } = axis;
    const shouldStartOnTick = _.isNil(extremes.min) && dataMin > min;
    const shouldEndOnTick = _.isNil(extremes.max) && dataMax < max;
    axis.options.startOnTick = shouldStartOnTick;
    axis.options.endOnTick = shouldEndOnTick;
    if (!shouldStartOnTick || !shouldEndOnTick) {
        axis.update({
            startOnTick: shouldStartOnTick,
            endOnTick: shouldEndOnTick,
        }, false);
    }
    axis.setExtremes(extremes.min, extremes.max, true);
}
export function setAxisRange(chart, chartModel) {
    if (chart.userOptions.chart.type.toUpperCase() === ChartType.Treemap ||
        chart.userOptions.chart.type.toUpperCase() === ChartType.Heatmap ||
        chartModel.config.chartType === ChartType.Kpi) {
        return false;
    }
    const axisProperties = getAxisProperties(chartModel);
    if (axisProperties) {
        let isUpdated = false;
        axisProperties.forEach(axisObj => {
            const { yAxisRange, axisType } = axisObj.properties;
            chart.axes.forEach(axis => {
                if (axis.userOptions.id === axisObj.id &&
                    axis.hasVisibleSeries) {
                    const isNilRange = _.isNil(yAxisRange === null || yAxisRange === void 0 ? void 0 : yAxisRange.min) && _.isNil(yAxisRange === null || yAxisRange === void 0 ? void 0 : yAxisRange.max);
                    if (!_.isNil(yAxisRange) && !isNilRange) {
                        const props = chart.userOptions.chart.props;
                        const updatedYAxisRange = getUpdatedAxisRangeBasedOnTicks(axisObj.id, props, yAxisRange, axis);
                        isUpdated = true;
                        updateExtremes(axis, updatedYAxisRange);
                    }
                    else if (axisType === AxisTypes.Y) {
                        const { dataMin, dataMax, min, max } = axis;
                        axis.update({
                            startOnTick: dataMin > min,
                            endOnTick: dataMax < max,
                        }, false);
                    }
                }
            });
        });
        return isUpdated;
    }
    return false;
}
export function updateChartZoom(chart, chartModel) {
    const chartProperties = getChartProperties(chartModel);
    if (!chart || !isZoomedIn(chartModel)) {
        return false;
    }
    const xAxisExtremes = chartProperties.axisExtremes.x;
    const yAxisExtremes = chartProperties.axisExtremes.y;
    chart.setExtremes(xAxisExtremes, yAxisExtremes);
    if (getEnvFlags().isFlipTooltipToContextMenuEnabled) {
        updateClipProperty(chart, true);
    }
    return true;
}
function configureAxisControls(chartEl, eventsMap, chartModel) {
    const isXAxisVertical = chartTypeSpecificationService.isXAxisVertical(getChartType(chartModel));
    const popoverXDirection = isXAxisVertical
        ? Orientation.Right
        : Orientation.Top;
    const isYAxisVertical = !isXAxisVertical;
    const xAxes = getXAxes(chartModel);
    const yAxesInChart = getYAxes(chartModel);
    const axisIdsToControllers = xAxes
        .map(xAxis => {
        const columns = xAxis.properties.linkedColumns.map(colId => getColumn(chartModel, colId));
        return { axisId: xAxis.id, columns, direction: popoverXDirection };
    })
        .concat(yAxesInChart.map((yAxis, index) => {
        let direction;
        if (isYAxisVertical) {
            direction =
                index === 0 ? Orientation.Right : Orientation.Left;
        }
        else {
            direction =
                index === 0 ? Orientation.Top : Orientation.Bottom;
        }
        const columns = yAxis.properties.linkedColumns.map(colId => getColumn(chartModel, colId));
        return { axisId: yAxis.id, columns, direction };
    }))
        .reduce((acc, item) => {
        acc[item.axisId] = {};
        return acc;
    }, {});
    const onAxisClick = eventsMap.get(ChartEventTypes.AxisClick);
    const onColumnSort = eventsMap.get(ChartEventTypes.ColumnSort);
    bindAxisEvents(chartEl, chartModel, onAxisClick, onColumnSort);
}
export function updateChartModelProperties(chartObj, chartModel, dataModel, chartConfig, chart, updateChartProperties, updateChartPropertiesWithOverridenColors) {
    var _a;
    const chartType = getChartType(chartModel);
    const { isMultiColorSeries } = configOptions(chartType, chartModel);
    let seriesColors = getSeriesColorsMap(chartModel);
    let needsUpdate = chartConfig.isSeriesColorUpgradedNeeded;
    let isColorUpdatedNeeded = false;
    chart.series.forEach(serie => {
        const seriesId = (serie.userOptions || serie).blinkSeriesId;
        if (isMultiColorSeries) {
            const pointArray = serie.points;
            pointArray.forEach(point => {
                const serieColor = getColorBySerieName(seriesColors, point.name);
                if (!serieColor ||
                    (point.color && serieColor !== point.color)) {
                    seriesColors = getUpdatedSeriesColor(seriesColors, point.name, point.color);
                    needsUpdate = true;
                    isColorUpdatedNeeded = true;
                }
            });
        }
        else {
            const serieColor = getColorBySerieName(seriesColors, seriesId);
            if (!serieColor && seriesId !== undefined) {
                seriesColors = getUpdatedSeriesColor(seriesColors, seriesId, serie.color);
                needsUpdate = true;
                isColorUpdatedNeeded = true;
            }
        }
        const chartModelSerie = getSerie(dataModel, serie.userOptions.name);
        if (chartModelSerie && !chartModelSerie.color) {
            chartModelSerie.color = serie.color;
        }
    });
    let updatedClientState = { ...chartModel.vizProp };
    if (isColorUpdatedNeeded) {
        needsUpdate = true;
        updatedClientState = {
            ...updatedClientState,
            systemSeriesColors: seriesColors,
        };
    }
    const responsiveLayoutPreference = chartConfig._responsiveLayoutPreference;
    let chartProperties = updatedClientState.chartProperties || {};
    if ((_a = chartModel.customOverridesConfig) === null || _a === void 0 ? void 0 : _a.isDataLabelsEnabledByDefault) {
        needsUpdate = true;
        chartProperties = {
            ...chartProperties,
            allLabels: true,
            showStackedLabels: true,
        };
        updatedClientState.columnProperties = updatedClientState.columnProperties.map(column => {
            var _a;
            if (_.isNil((_a = column.columnProperty) === null || _a === void 0 ? void 0 : _a.dataLabels)) {
                return {
                    ...column,
                    columnProperty: {
                        ...column.columnProperty,
                        dataLabels: true,
                    },
                };
            }
            return column;
        });
    }
    if (chartProperties.responsiveLayoutPreference !==
        responsiveLayoutPreference &&
        (responsiveLayoutPreference === ChartResponsiveLayout.AutoOn ||
            responsiveLayoutPreference === ChartResponsiveLayout.AutoOff)) {
        needsUpdate = true;
        updatedClientState = {
            ...updatedClientState,
            chartProperties: {
                ...chartProperties,
                responsiveLayoutPreference,
            },
        };
    }
    if (needsUpdate) {
        updateChartProperties(updatedClientState);
        chartConfig.isSeriesColorUpgradedNeeded = false;
        _.set(chartObj.getVizModel(), 'vizProp', updatedClientState);
    }
    const hasColorOverrides = !!getColorPaletteFromLegendOverrides(chartModel);
    if (hasColorOverrides) {
        updateChartPropertiesWithOverridenColors(updatedClientState);
    }
}
export function chartUIRenderHighchart(chartObj, chartEl, chartModel, dataModel, eventsMap, config, env) {
    try {
        const ctrBoundingBox = chartEl.getBoundingClientRect();
        const notifyRenderComplete = () => {
            executeEventHandler(eventsMap, ChartEventTypes.RenderComplete);
        };
        const postRenderTasks = () => {
            configureAxisControls(chartEl, eventsMap, chartModel);
        };
        let isRenderCompleteTriggered = false;
        const onRender = (_, isChartSecondaryRenderComplete = false) => {
            if (isRenderCompleteTriggered) {
                postRenderTasks();
                return;
            }
            if (isChartSecondaryRenderComplete) {
                isRenderCompleteTriggered = true;
            }
            if (!isRenderCompleteTriggered) {
                return;
            }
            postRenderTasks();
            notifyRenderComplete();
        };
        const updateChartProperties = (vizProp) => {
            executeEventHandler(eventsMap, ChartEventTypes.UpdateClientState, [
                vizProp,
                false,
                false,
            ]);
        };
        const updateChartPropertiesWithOverridenColors = (vizProp) => {
            executeEventHandler(eventsMap, ChartEventTypes.UpdateClientState, [
                vizProp,
                false,
                true,
                true,
            ]);
        };
        const blinkHighchartConfig = generateHighchartConfig(chartModel, dataModel, ctrBoundingBox, eventsMap, config, env, onRender);
        const chartConfig = blinkHighchartConfig.build();
        setChartContainer(chartConfig, chartEl);
        logger.debug('chartConfig', chartConfig.toString(), chartConfig);
        const blinkChart = new BlinkHighchart(chartConfig, onRender);
        const lightModeColors = blinkHighchartConfig.getLightModeColors();
        if (isRadiantThemeEnabled() && lightModeColors.length > 0) {
            const unsubscribe = subscribeToThemeChanges(() => {
                const paletteColors = isDarkModeActive()
                    ? generateDarkModePalette(lightModeColors)
                    : lightModeColors;
                const resolveColor = createSeriesColorResolver(chartModel);
                blinkChart.series.forEach((series, seriesIdx) => {
                    var _a;
                    const seriesOptions = series.options;
                    if (seriesOptions === null || seriesOptions === void 0 ? void 0 : seriesOptions.colorByPoint) {
                        series.data.forEach((point, pointIdx) => {
                            const pointName = point === null || point === void 0 ? void 0 : point.name;
                            const resolved = resolveColor(pointName);
                            const color = resolved !== null && resolved !== void 0 ? resolved : paletteColors[pointIdx % paletteColors.length];
                            if (color) {
                                point.update({ color }, false);
                            }
                        });
                        return;
                    }
                    const seriesId = seriesOptions === null || seriesOptions === void 0 ? void 0 : seriesOptions.blinkSeriesId;
                    const resolved = (_a = resolveColor(seriesId)) !== null && _a !== void 0 ? _a : resolveColor(series.name);
                    if (resolved) {
                        series.update({ color: resolved }, false);
                        return;
                    }
                    if (seriesIdx >= paletteColors.length)
                        return;
                    series.update({ color: paletteColors[seriesIdx] }, false);
                });
                blinkChart.redraw(false);
            });
            getHighchartsObject().addEvent(blinkChart, 'destroy', unsubscribe);
        }
        updateChartModelProperties(chartObj, chartModel, dataModel, blinkHighchartConfig, blinkChart, updateChartProperties, updateChartPropertiesWithOverridenColors);
        let needRedraw = setAxisRange(blinkChart, chartModel);
        if (!needRedraw) {
            needRedraw = updateChartZoom(blinkChart, chartModel);
        }
        if (needRedraw) {
            blinkChart.lightRedraw();
        }
        return Promise.resolve(blinkChart);
    }
    catch (err) {
        if (err.message === ErrorTypes.DATA_POINTS_LIMIT_EXCEEDED) {
            const throwErrorException = eventsMap.get(ChartEventTypes.ExceptionRaised);
            throwErrorException(err.message);
        }
        else {
            logger.error('Failed to load the chart due to highchart errors', err);
            throw new Error(err);
        }
    }
}
const toggleAxesTitle = (enabled, chart, config) => {
    if (!chart.xAxis || !chart.yAxis) {
        return;
    }
    chart.xAxis.forEach((axis, index) => {
        if (enabled) {
            axis.update({ title: { text: config.cachedXAxisHtml[index] } }, true);
        }
        else {
            axis.update({ title: { text: ' ' } }, true);
        }
    });
    chart.yAxis.forEach((axis, index) => {
        if (enabled) {
            axis.update({ title: { text: config.cachedYAxisHtml[index] } }, true);
        }
        else {
            axis.update({ title: { text: ' ' } }, true);
        }
    });
};
function updateAxisGridLine(axis, enabled) {
    const gridLineConfig = enabled ? onGridline : offGridline;
    axis.update(gridLineConfig, false);
}
export function isDataLabelsEnabled() {
    return !!this.series.find(serie => !!_.get(serie, 'options.dataLabels.enabled'));
}
export function isStackLabelsEnabled() {
    return !!_.get(this, 'options.yAxis.0.stackLabels.enabled');
}
export function updateChartPropertiesOnResize(chart, vizModel, dataModel) {
    var _a;
    const chartPlotHeight = chart.plotHeight;
    const chartPlotWidth = chart.plotWidth;
    let newHeight = chartPlotHeight;
    let newWidth = chartPlotWidth;
    if (isXAxisVertical(getChartType(vizModel))) {
        newHeight = chartPlotWidth;
        newWidth = chartPlotHeight;
    }
    const shouldHideChartComponents = isHideChartComponentsForSmallViewport();
    const dimensions = (_a = chart.container) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
    const { showXAxisLabels, showXAxisName, showYAxisLabels, showYAxisName, } = getAxisPropertiesVisibility(dimensions);
    const showDataLabels = shouldShowDataLabels(vizModel, dataModel, getEnvFlags().maxLabelCount, getEnvFlags().dataBatchSize, shouldHideChartComponents ? dimensions : null);
    const effectiveShowDataLabels = configOptions(getChartType(vizModel), vizModel).dataLabelsAlwaysEnabled
        ? true
        : showDataLabels;
    const hasEnoughSpaceForStackLabels = !shouldHideChartComponents ||
        hasEnoughSpaceForDataLabels(dimensions, getChartType(vizModel));
    if (getShowStackedLabels(vizModel)) {
        chart.setStackLabelVisibility(hasEnoughSpaceForStackLabels && showDataLabels);
    }
    updateDataLabels(chart, vizModel, dataModel, undefined, effectiveShowDataLabels);
    chart.xAxis.forEach(axis => {
        var _a, _b, _c, _d;
        const axisId = (_a = axis.userOptions) === null || _a === void 0 ? void 0 : _a.id;
        const originalState = axisId
            ? (_c = (_b = chart).getOriginalAxisState) === null || _c === void 0 ? void 0 : _c.call(_b, axisId)
            : undefined;
        const customOverride = originalState === null || originalState === void 0 ? void 0 : originalState.labelsEnabled;
        const labelsEnabled = _.isNil(customOverride) ? true : customOverride;
        const { axisSettings: { visible = true } = {} } = getAxisAdvancedPropertiesById(vizModel.vizProp, (_d = axis.userOptions) === null || _d === void 0 ? void 0 : _d.id) || {};
        const maxLabelWidth = chartTypeSpecificationService.getXAxisLabelMaxWidth(newHeight, !isXAxisVertical(vizModel.config.chartType));
        axis.update({
            visible: visible && showXAxisName,
            labels: {
                enabled: labelsEnabled && showXAxisLabels,
                style: {
                    width: maxLabelWidth,
                    textOverflow: maxLabelWidth ? 'ellipsis' : undefined,
                },
            },
            title: { style: { width: `${newWidth}px` } },
        });
    });
    chart.yAxis.forEach(axis => {
        var _a, _b, _c, _d, _e, _f;
        const axisId = (_a = axis.userOptions) === null || _a === void 0 ? void 0 : _a.id;
        const originalState = axisId
            ? (_c = (_b = chart).getOriginalAxisState) === null || _c === void 0 ? void 0 : _c.call(_b, axisId)
            : undefined;
        const customOverride = originalState === null || originalState === void 0 ? void 0 : originalState.labelsEnabled;
        const labelsEnabled = _.isNil(customOverride) ? true : customOverride;
        const axisSettings = ((_e = getAxisAdvancedPropertiesById(vizModel.vizProp, (_d = axis.userOptions) === null || _d === void 0 ? void 0 : _d.id)) === null || _e === void 0 ? void 0 : _e.axisSettings) || {};
        const visible = (_f = axisSettings.visible) !== null && _f !== void 0 ? _f : true;
        const hideAxisComponents = !visible || !showYAxisName;
        axis.update({
            ...(hideAxisComponents
                ? { lineWidth: 0, tickWidth: 0, minorTickWidth: 0 }
                : originalState
                    ? {
                        lineWidth: originalState.lineWidth,
                        tickWidth: originalState.tickWidth,
                        minorTickWidth: originalState.minorTickWidth,
                    }
                    : {}),
            labels: {
                enabled: !hideAxisComponents && labelsEnabled && showYAxisLabels,
            },
            title: hideAxisComponents
                ? { text: '', style: { width: `${newHeight}px` } }
                : originalState
                    ? {
                        text: originalState.titleText,
                        style: { width: `${newHeight}px` },
                    }
                    : { style: { width: `${newHeight}px` } },
        });
    });
}
//# sourceMappingURL=highchart-chart-util.js.map